import { CancerResult, CancerDiseaseResult } from "interface/cancer"
import { CancerResultViewType } from './types';
import { numberFormat, round } from 'shared/util';

import {
  Box,
  Table, TableBody, TableCell, TableContainer, TableContainerProps, TableHead, TableRow
} from "@mui/material";
import { CSSProperties } from '@mui/material/styles/createMixins';

const CancerCountCell = (props: {
  value: number | null
}) => {
  if (props.value) {
    return <TableCell align="right">{ numberFormat(round(props.value, 1)) }</TableCell>
  } else {
    return <TableCell align="center" style={{ background: 'GhostWhite' }} />
  }
}
interface CancerResultColumn {
  has?: boolean,
  title: string,
  val: (record: CancerResult) => number | null
}

type CancerResultTableProps = {
  list: CancerResult[],
  disease: CancerDiseaseResult,
  viewType: number
} & TableContainerProps
export const CancerResultTable = ({
  list,
  disease,
  viewType
}: CancerResultTableProps) => {
  const columns: { [index: string]: CancerResultColumn } = 
    (viewType === CancerResultViewType.Stage) ? {
      incubation: { val: (record) => record.stages?.incubation, title: '潜伏期' },
      stage0    : { val: (record) => record.stages?.stage0    , title: '０期' },
      stage0a   : { val: (record) => record.stages?.stage0a   , title: '０a期'  },
      stage0is  : { val: (record) => record.stages?.stage0is  , title: '０is期' },
      stage1    : { val: (record) => record.stages?.stage1    , title: 'Ⅰ期' },
      stage1a   : { val: (record) => record.stages?.stage1a   , title: 'ⅠＡ期' },
      stage1b   : { val: (record) => record.stages?.stage1b   , title: 'ⅠＢ期' },
      stage1c   : { val: (record) => record.stages?.stage1c   , title: 'ⅠＣ期' },
      stage2    : { val: (record) => record.stages?.stage2    , title: 'Ⅱ期' },
      stage2a   : { val: (record) => record.stages?.stage2a   , title: 'ⅡＡ期' },
      stage2b   : { val: (record) => record.stages?.stage2b   , title: 'ⅡＢ期' },
      stage2c   : { val: (record) => record.stages?.stage2c   , title: 'ⅡＣ期' },
      stage3    : { val: (record) => record.stages?.stage3    , title: 'Ⅲ期'   },
      stage3a   : { val: (record) => record.stages?.stage3a   , title: 'ⅢＡ期' },
      stage3b   : { val: (record) => record.stages?.stage3b   , title: 'ⅢＢ期' },
      stage3c   : { val: (record) => record.stages?.stage3c   , title: 'ⅢＣ期' },
      stage4    : { val: (record) => record.stages?.stage4    , title: 'Ⅳ期' },
      stage4a   : { val: (record) => record.stages?.stage4a   , title: 'ⅣＡ期' },
      stage4b   : { val: (record) => record.stages?.stage4b   , title: 'ⅣＢ期' },
      stage4c   : { val: (record) => record.stages?.stage4c   , title: 'ⅣＣ期' },
      prestage  : { val: (record) => record.stages?.prestage  , title: '施術前' },
      total     : { val: (record) => {
        return (
          (record.stages?.stage0 ?? 0) + 
          (record.stages?.stage1 ?? 0) + 
          (record.stages?.stage2 ?? 0) + 
          (record.stages?.stage3 ?? 0) + 
          (record.stages?.stage4 ?? 0)
        );
      }, title: '合計' },
      prediction: { val: (record) => {
        return (
          ((record.stages?.stage0 ?? 0) * disease.factor.stage0 / 100) +
          ((record.stages?.stage1 ?? 0) * disease.factor.stage1 / 100) +
          ((record.stages?.stage2 ?? 0) * disease.factor.stage2 / 100) +
          ((record.stages?.stage3 ?? 0) * disease.factor.stage3 / 100) +
          ((record.stages?.stage4 ?? 0) * disease.factor.stage4 / 100)
        );
      }, title: '再発F値' },
    } : (viewType === CancerResultViewType.AgeAndGenders) ? {
      age0to14  : { val: (record) => record.ages?.age0to14   , title: '15歳未満' },
      age15to39 : { val: (record) => record.ages?.age15to39  , title: '15～39歳' },
      age40to64 : { val: (record) => record.ages?.age40to64  , title: '40～64歳' },
      age65to74 : { val: (record) => record.ages?.age65to74  , title: '65～74歳' },
      age75to84 : { val: (record) => record.ages?.age75to84  , title: '75～84歳' },
      age85over : { val: (record) => record.ages?.age85over  , title: '85歳以上' },
      male      : { val: (record) => record.gender?.male       , title: '男性' },
      female    : { val: (record) => record.gender?.female     , title: '女性' },
    } : {};

  for(const record of list) {
    for(const columnName in columns) {
      const column = columns[columnName];
      if (!column.has && column.val(record)) {
        column.has = true;
      }
    }
  }
  const validColumnNames = Object.keys(columns).filter((columnName) => columns[columnName].has);
  if (validColumnNames.length === 0) return <></>;

  const headerCellStyle: CSSProperties = {
    width: 0,
    whiteSpace: 'nowrap'
  }
  return (
    <Box padding={2}>
      <TableContainer>
        <Table aria-label="cancer result table" stickyHeader size="small" >
          <TableHead>
            <TableRow>
              <TableCell style={headerCellStyle} >年度</TableCell>
              { validColumnNames.map((columnName) => {
                const column = columns[columnName];
                return <TableCell key={`column_${columnName}`} style={headerCellStyle} >{column.title}</TableCell>
              }) }
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            { list.map((record) => (
              <TableRow key={record.recordId}>
                <TableCell align="center">{record.year}</TableCell>
                { validColumnNames.map((columnName) => {
                  const column = columns[columnName];
                  return <CancerCountCell key={`cell_${columnName}_${record.recordId}`} value={column.val(record)} />
                }) }
              </TableRow>
            )) }
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}